import { useEffect } from 'react';
import { X } from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useTranslation } from '@doccareplus/shared/lib';
import { GlobalSearchPanel } from '../shared/GlobalSearchPanel';
import { useGlobalSearchController, type UICommand } from '../shared/useGlobalSearchController';

export interface GlobalSearchSheetProps {
  open: boolean;
  onClose: () => void;
  /** Static navigation pages + quick actions, supplied (and capability-filtered) by the mobile shell. */
  commands: UICommand[];
}

/**
 * GlobalSearchSheet — the mobile global-search surface: a full-screen search view slid up from the
 * header search bar. Shares the exact search behaviour with the desktop palette via the common
 * {@link GlobalSearchPanel}; only the chrome differs (full-screen sheet vs centered overlay).
 */
export function GlobalSearchSheet({ open, onClose, commands }: GlobalSearchSheetProps) {
  const { t } = useTranslation('common');
  const reduceMotion = useReducedMotion();
  const controller = useGlobalSearchController(commands, onClose);
  const { setTerm } = controller;

  useEffect(() => {
    if (open) setTerm('');
  }, [open, setTerm]);

  return (
    <AnimatePresence>
      {open && (
        <motion.section
          key="global-search-sheet"
          initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 24 }}
          transition={reduceMotion ? { duration: 0.12 } : { duration: 0.2, ease: [0.22, 0.61, 0.36, 1] }}
          role="dialog"
          aria-modal="true"
          aria-label={t('search.title')}
          className="fixed inset-0 z-50 mx-auto flex max-w-[430px] flex-col bg-[var(--bg)]"
          style={{ paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        >
          <div className="flex items-center justify-between px-4 pb-1 pt-2">
            <h2 className="text-[17px] font-bold text-[var(--text)]">{t('search.title')}</h2>
            <button
              type="button"
              aria-label={t('search.close')}
              onClick={onClose}
              className="flex h-9 w-9 items-center justify-center rounded-[11px] border-none bg-[var(--surface-2)] text-[var(--text-2)]"
            >
              <X className="h-[18px] w-[18px]" aria-hidden="true" />
            </button>
          </div>
          <div className="flex min-h-0 flex-1 flex-col">
            <GlobalSearchPanel controller={controller} />
          </div>
        </motion.section>
      )}
    </AnimatePresence>
  );
}
